import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { faker } from "@faker-js/faker";
import clsx from "clsx";
import { addMinutes, addMonths, addYears, differenceInDays, differenceInMonths, differenceInYears, format, parse, parseISO } from "date-fns";
import { isArray, isString, keys } from "lodash";
import React from "react";
import RBACFlag from "../components/core/RBACFlag";
import { DIAGNOSTICS_SERVICE_IDS } from "../utils/bookingServiceSelection";
import { ICityOnboardFormFields } from "../components/routes/auth_routes/Configuration/CitySetup/CityOnboard";

const POStatusTextColor = (status: string, background: boolean = false) => {
  if (status === "pre_draft") {
    return ` ${background ? "text-black bg-[#FFF2E2]" : "text-blue-400"}`;
  } else if (status === "draft") {
    return `${background ? "text-black bg-[#FFF2E2]" : "text-[#C13BF0]"}`;
  } else if (status === "pending_invoice_upload") {
    return `${background ? " text-black bg-[#FFF6C5]" : "text-[#FFF6C5]"}`;
  } else if (status === "pending_invoice_verification") {
    return `${background ? " text-black bg-[#FFF6C5]" : "text-[#FFF6C5]"}`;
  } else if (status === "awaiting_grn") {
    return `${background ? " text-black bg-[#FFD9E1]" : "text-[#FFD9E1]"}`;
  } else if (status === "closed") {
    return `${background ? " text-black bg-[#E0F5ED]" : "text-[#E0F5ED]"}`;
  } else if (status === "issue_in_invoice_upload") {
    return `${background ? " text-black bg-[#FF7593]" : "text-[#FF7593]"}`;
  } else if (status === "issue_in_grn") {
    return `${background ? " text-black bg-[#FF7593]" : "text-[#FF7593]"}`;
  } else if (status === "sent_to_finance") {
    return `${background ? " text-black bg-[#33B081]" : "text-[#33B081]"}`;
  }
};
const getStatusColor = (status: string) => {
  switch (status) {
    case "Created":
      return { color: "#8F00FF" };
    case "Confirmed":
      return { color: "#058E34" };
    case "Ready for Pickup":
      return { color: "#1D63B6" };
    case "Picked":
      return { color: "#00A79D" };
    case "Delivered":
      return { color: "#058E34" };
    case "Undelivered":
      return { color: "#B78811" };
    case "Cancelled":
      return { color: "#DB1E4C" };
    case "Closed":
      return { color: "#058E34" };
    default:
      return {};
  }
};

const statusFormatLebel = (value?: string) =>
  value
    ? value
        .split("_")
        .map((word) =>
          word.toLowerCase() === "for"
            ? "For"
            : word.length <= 3
              ? word.toUpperCase()
              : word.charAt(0).toUpperCase() + word.slice(1),
        )
        .join(" ")
    : null;

const formatTo12Hour = (time: string) => {
  if (!time) return "";

  const [hourStr, minute] = time.split(":");
  let hour = Number(hourStr);

  const period = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12; // convert 0 → 12

  return `${hour}:${minute} ${period}`;
};

export type Person = {
  firstName: string;
  lastName: string;
  age: number;
  visits: number;
  progress: number;
  status: "relationship" | "complicated" | "single";
  subRows?: Person[];
};

function convertToReadableFormat(str) {
  return str
    ?.split("_")
    ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
    ?.join(" ");
}

const range = (len: number) => {
  const arr: number[] = [];
  for (let i = 0; i < len; i++) {
    arr.push(i);
  }
  return arr;
};

const newPerson = (): Person => {
  return {
    firstName: faker.person.firstName(),
    lastName: faker.person.lastName(),
    age: faker.number.int(40),
    visits: faker.number.int(1000),
    progress: faker.number.int(100),
    status: faker.helpers.shuffle<Person["status"]>(["relationship", "complicated", "single"])[0]!,
  };
};

export const useUtils = () => {
  const { org_services } = useAppSelector((state) => state.staticAPI);
  const { org_services_for_booking: orgServicesForBooking } = useAppSelector((state) => state.staticAPI);
  /* returns states for valid pet statuses */
  const getPetStatusCorrespondingBooleans = (status: string) => {
    const s = status?.toLocaleLowerCase();
    const statues = {
      active: {
        is_pet_active: true,
        is_pet_inactive: false,
        is_pet_deceased: false,
      },
      inactive: {
        is_pet_active: false,
        is_pet_inactive: true,
        is_pet_deceased: false,
      },
      deceased: {
        is_pet_active: false,
        is_pet_inactive: false,
        is_pet_deceased: true,
      },
      default: {
        is_pet_active: false,
        is_pet_inactive: false,
        is_pet_deceased: false,
      },
    };
    if (s !== "") {
      return statues[s];
    }
    return statues.default;
  };

  const imageUrlToDownload = (url: string, fileName: string) => {
    if (url) {
      // Trigger download
      window.open(url, "_blank");
    } else {
      // Open in a new tab

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const InvoiceStatusDataTransformed = (item: string): string => {
    if (item) {
      return item?.split("_")?.join(" ");
    }
    return "NA";
  };

  const hasAnyValidValue = (obj: any) => {
    if (!obj) return [];
    return keys(obj).filter((key: string) => {
      const value = obj[key];
      if (isString(value)) {
        return value.trim().length > 0;
      }
      if (isArray(value)) {
        return value.length > 0;
      }
      if (value === null || value === undefined) {
        return false;
      }
      return !!value; // Handles other truthy values like numbers or objects
    });
  };

  const convertDataToHTMLList = (data) => {
    const listItems = data?.map((comment) => `<li>${comment?.value}</li>`)?.join("");
    return `<ol>${listItems}</ol>`;
  };

  const handleBase64ToImageDownload = (base64String, fileName, file_extension) => {
    const base64 = `data:image/${file_extension};base64,${base64String}`;
    const filename = `${fileName}.${file_extension}`;
    // Validate Base64 format
    if (!base64.startsWith("data:image/")) {
      throw new Error("Invalid Base64 string");
    }

    // Split the Base64 string to get the MIME type and the actual Base64 data
    const parts = base64.split(",");
    if (parts.length !== 2) {
      throw new Error("Invalid Base64 string");
    }

    const mimeString = parts[0].match(/:(.*?);/)[1];
    const byteString = atob(parts[1]);

    // Convert Base64 string to binary data
    const byteNumbers = new Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteNumbers[i] = byteString.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeString });

    // Create an object URL from the Blob
    const url = URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger the download
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  function makeData(...lens: number[]) {
    const makeDataLevel = (depth = 0): Person[] => {
      const len = lens[depth]!;
      return range(len).map((d): Person => {
        return {
          ...newPerson(),
          subRows: lens[depth + 1] ? makeDataLevel(depth + 1) : undefined,
        };
      });
    };

    return makeDataLevel();
  }

  const base64ToValidURL = (base64String: string, fileType: string) => {
    return `data:${fileType};base64,${base64String}`;
  };

  const formatDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0"); // Ensure two digits
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Ensure two digits (Months are 0-indexed)
    const year = today.getFullYear();

    return `${day}-${month}-${year}`;
  };

  function veticDateFormat(expiry_date: string | number | null | undefined): string | null {
    if (!expiry_date) return null;

    let dateObj: Date | null = null;
    let includeTime = false;

    try {
      if (typeof expiry_date === "number") {
        // Handle epoch timestamp (assuming seconds, convert to milliseconds)
        dateObj = new Date(expiry_date * 1000);
        includeTime = true;
      } else if (typeof expiry_date === "string") {
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(expiry_date)) {
          // Handle ISO format like "2025-02-13T16:53:10"
          dateObj = new Date(expiry_date);
          includeTime = true;
        } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(expiry_date)) {
          // Handle MM/DD/YYYY format
          const [month, day, year] = expiry_date.split("/").map(Number);
          dateObj = new Date(year, month - 1, day);
        } else if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(expiry_date)) {
          // Handle "13-02-2025 17:34:07" (DD-MM-YYYY HH:MM:SS)
          const [datePart, timePart] = expiry_date.split(" ");
          const [day, month, year] = datePart.split("-").map(Number);
          const [hours, minutes] = timePart.split(":").map(Number);
          dateObj = new Date(year, month - 1, day, hours, minutes);
          includeTime = true;
        } else if (/^\d{2}-\d{2}-\d{4}$/.test(expiry_date)) {
          // Handle "14-02-2025" (DD-MM-YYYY without time)
          const [day, month, year] = expiry_date.split("-").map(Number);
          dateObj = new Date(year, month - 1, day);
        } else if (
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(expiry_date) ||
          /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(expiry_date)
        ) {
          // Handle both "2025-06-03T09:00:15" and "2025-06-03 09:00:15"
          dateObj = new Date(expiry_date.replace(" ", "T")); // Convert to valid ISO
          includeTime = true;
        } else {
          // Fallback for any other formats
          dateObj = new Date(expiry_date);
        }
      }

      if (!dateObj || isNaN(dateObj.getTime())) {
        return null;
      }

      // Month mapping
      const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

      const date = String(dateObj.getDate()).padStart(2, "0");
      const month = monthNames[dateObj.getMonth()];
      // const year = dateObj.getFullYear();
      const year = dateObj.getFullYear().toString().slice(-2);
      if (includeTime) {
        let hours = dateObj.getHours();
        const minutes = String(dateObj.getMinutes()).padStart(2, "0");
        const amPm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12; // Convert 24-hour format to 12-hour

        return `${date} ${month} ${year} ${hours}:${minutes} ${amPm}`;
      }

      return `${date} ${month} ${year}`;
    } catch (error) {
      console.error("Invalid date input:", expiry_date, error);
      return null;
    }
  }

  function veticEpocToTime(expiry_date: string | number | null | undefined): string | null {
    if (!expiry_date) return null;

    let dateObj: Date | null = null;

    try {
      if (typeof expiry_date === "number") {
        // Handle epoch timestamp (assuming seconds, convert to milliseconds)
        dateObj = new Date(expiry_date * 1000);
      } else if (typeof expiry_date === "string") {
        if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}$/.test(expiry_date)) {
          // Handle ISO or "YYYY-MM-DD HH:MM:SS"
          dateObj = new Date(expiry_date.replace(" ", "T"));
        } else if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(expiry_date)) {
          // Handle "DD-MM-YYYY HH:MM:SS"
          const [datePart, timePart] = expiry_date.split(" ");
          const [day, month, year] = datePart.split("-").map(Number);
          const [hours, minutes, seconds] = timePart.split(":").map(Number);
          dateObj = new Date(year, month - 1, day, hours, minutes, seconds);
        } else {
          // Fallback for other formats
          dateObj = new Date(expiry_date);
        }
      }

      if (!dateObj || isNaN(dateObj.getTime())) return null;

      // --- Extract time ---
      let hours = dateObj.getHours();
      const minutes = String(dateObj.getMinutes()).padStart(2, "0");
      const amPm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12; // Convert 24-hour to 12-hour

      return `${hours}:${minutes} ${amPm}`;
    } catch (error) {
      console.error("Invalid date input:", expiry_date, error);
      return null;
    }
  }

  const fileListToBase64 = async (
    fileList: FileList,
  ): Promise<{ base_64_string: string; file_name: string; file_extension: string }[]> => {
    const filesArray = Array.from(fileList);
    const base64Promises = filesArray.map((file) => {
      return new Promise<{ base_64_string: string; file_name: string; file_extension: string }>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () =>
          resolve({
            base_64_string: `${reader.result}`.split(",")[1] as string,
            file_name: file?.name?.split(".")[0],
            file_extension: `${file.type.split("/")[1]}`,
          });
        reader.onerror = (error) => reject(error);
      });
    });

    return Promise.all(base64Promises)
      .then((base64Objects) => {
        return base64Objects;
      })
      .catch(() => {
        return [];
      });
  };

  const dateTimeToEpoc = (start_date, start_time_h, start_time_m) => {
    // Create the date in IST directly
    const startDateTimeIST = new Date(`${start_date}T${start_time_h}:${start_time_m}:00+05:30`); // IST

    // Get the epoch time in seconds
    const startEpoch = Math.floor(startDateTimeIST.getTime() / 1000);

    return startEpoch;
  };

  function cleanURLPathname(url: string) {
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    const urlWithoutQueryParams = url.split("?")[0];
    const urlParts = urlWithoutQueryParams.split("/");
    const cleanedParts = urlParts.filter((part) => !uuidRegex.test(part));
    const cleanedPathname = cleanedParts?.join("/");

    return cleanedPathname;
  }

  const convertFarenhiteToCelcius = (temperature: number) => {
    const celsius = (temperature - 32) * (5 / 9);
    return parseFloat(celsius.toFixed(2));
  };

  const convertCelciusToFarenhite = (temperature: number) => {
    const fahrenheit = temperature * (9 / 5) + 32;
    return parseFloat(fahrenheit.toFixed(2));
  };

  /* converts yyyy-DD-MM format to ISOString format  */
  const convertYDMtoDOBformat = (years = 0, months = 0, days = 0) => {
    const currentDate = new Date();
    const targetDate = new Date(
      currentDate.getFullYear() - years,
      currentDate.getMonth() - months,
      currentDate.getDate() - days,
    );

    // Format the target date as "YYYY-MM-DD" using LOCAL components.
    // Using toISOString() here converts to UTC and shifts the date back a day
    // for timezones ahead of UTC (e.g. IST +5:30), making the pet a day older.
    const yyyy = targetDate.getFullYear();
    const mm = String(targetDate.getMonth() + 1).padStart(2, "0");
    const dd = String(targetDate.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  /* helps converting date to yyyy-MM-dd format  */
  const convertToValidDateStringFormat = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  /* Masks mobile middle numbers only shows last digits */
  const maskMobile = (mobile: string) => {
    if (mobile) {
      const mask = new Array(6)?.fill("x");
      return [mobile[0], mobile[1], ...mask, mobile[mobile.length - 2], mobile[mobile.length - 1]].join("");
    }
    return null;
  };

  /* Helps merging tailwindcss classes */

  const classNames = (...classes: string[]) => {
    return clsx(classes);
  };

  /* Convert Dangerous HTML <li> some comment </li> to Array<string> to process it */

  const convertNoteToArray = (value: string) => {
    // Check if value is undefined or null
    return HTMLLiTagsToArray(value);
  };

  /* Truncates string to specific length   */

  const truncateString = (str: string, maxLength: number) => {
    if (str?.length > maxLength) {
      return str?.slice(0, maxLength) + "...";
    }
    return str;
  };

  /* convert valid timestring HH:MM string to valid 12 HR format with AM PM  */
  const convertTime12HrFormat = (timeString: string) => {
    const [hours, minutes] = timeString.split(":");

    // Convert hours to a number
    const hoursNum = parseInt(hours);

    // Determine whether it's AM or PM
    const period = hoursNum >= 12 ? "PM" : "AM";

    // Calculate the 12-hour format hours
    const hours12Hour = hoursNum % 12 || 12;

    // Format the time in 12-hour format
    const time12Hour = `${hours12Hour}:${minutes} ${period}`;
    return time12Hour;
  };

  /* Converts createdAt timestamp to valid 12HR format string */
  const to12HrFormat = (createdAt: string) => {
    const date = new Date(createdAt);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;
    const formattedTime = `${formattedHours}:${formattedMinutes} ${ampm}`;
    return formattedTime;
  };

  /*  convert yyyy-MM-dd dob format to valid Date Yrs and Months */
  const dateToAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return "";

    const dob = new Date(dateOfBirth);
    if (isNaN(dob.getTime())) return "";

    const currentDate = new Date();
    const ageInDays = differenceInDays(currentDate, dob);
    if (ageInDays < 0) return "0d";

    // If the age is less than 30 days, return the age in days
    if (ageInDays < 30) {
      return ageInDays + "d";
    }

    // Calendar-accurate split, sharing the same clock reading
    const { years: ageInYears, months: remainingMonths } = DOBToAge(dateOfBirth, currentDate);

    // Format the age
    let formattedAge = "";

    // Add years if greater than 0
    if (ageInYears > 0) {
      formattedAge += ageInYears + "Y ";
    }

    // Add remaining months if greater than 0 or if years are 0
    if (remainingMonths > 0 || ageInYears === 0) {
      formattedAge += remainingMonths + "M ";
    }

    return formattedAge.trim(); // Trim to remove any extra whitespace
  };

  const DOBToAge = (birthdate: string, currentDate: Date = new Date()) => {
    if (!birthdate) return { years: 0, months: 0 };
    const birthDate = new Date(birthdate);
    if (isNaN(birthDate.getTime())) return { years: 0, months: 0 };

    // Derive years and months from one calendar-month count so they can't drift apart
    const totalMonths = differenceInMonths(currentDate, birthDate);
    if (totalMonths < 0) return { years: 0, months: 0 };

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    return { years, months };
  };

  const ISOToRelativeTime = (dateAndTime: string) => {
    const ISODateAndTime = parseISO(dateAndTime);
    const convertedTime = format(ISODateAndTime, "hh:mm a");
    return convertedTime;
  };

  const addAMPMToTime = (time: string) => {
    if (!time) return "Invalid Time";
    const normalizedTime = /^\d{2}:\d{2}$/.test(time) ? `${time}:00` : time;
    return format(parse(normalizedTime, "HH:mm:ss", new Date()), "hh:mm a");
  };

  // "09:00" -> "9:00 am", "19:00" -> "7:00 pm"
  const formatShiftTime = (time?: string): string => {
    const [hourStr, minuteStr = "00"] = (time ?? "").split(":");
    const hour = parseInt(hourStr, 10);
    if (Number.isNaN(hour)) return "";
    const period = hour < 12 ? "am" : "pm";
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${minuteStr} ${period}`;
  };

  // Multiple shift windows joined with "&": "9:00 am to 7:00 pm & 9:00 pm to 11:59 pm"
  const formatShiftTimings = (timings?: { start_time: string; end_time: string }[]): string =>
    (timings ?? [])
      .filter((slot) => slot?.start_time && slot?.end_time)
      .map((slot) => `${formatShiftTime(slot.start_time)} to ${formatShiftTime(slot.end_time)}`)
      .join(" & ");

  const ISODatetoHuman = (dateString: string) => {
    if (!dateString) return "Invalid Date";
    return format(parseISO(dateString), "MMMM d, yyyy");
  };

  /* Redux States Utils Starts */
  const isSameId = (left: any, right: any) => String(left ?? "") === String(right ?? "");

  const getServiceSource = () => {
    const hasBookingServices =
      (Array.isArray(orgServicesForBooking) && orgServicesForBooking.length > 0) ||
      (!!orgServicesForBooking &&
        !Array.isArray(orgServicesForBooking) &&
        Object.keys(orgServicesForBooking).length > 0);

    return hasBookingServices ? orgServicesForBooking : (org_services ?? []);
  };

  const parseServiceSource = () => {
    const source: any = getServiceSource();
    const serviceTypesList: any[] = Array.isArray(source)
      ? source.filter((item: any) => Array.isArray(item?.services))
      : Object.values(source?.service_types ?? {});
    const flatServicesList: any[] = Array.isArray(source)
      ? source.filter((item: any) => item?.service_id && item?.service_name)
      : [];

    return { source, serviceTypesList, flatServicesList };
  };

  const flattenSubServices = (services: any[] = []) =>
    Array.from(
      new Map(
        services
          .flatMap((service: any) => service?.sub_services ?? [])
          .map((subService: any) => [subService?.sub_service_id, subService]),
      ).values(),
    );

  const findSubServiceInServices = (services: any[] = [], subServiceId: string) =>
    services
      .flatMap((service: any) => service?.sub_services ?? [])
      .find((subService: any) => isSameId(subService?.sub_service_id, subServiceId));

  const getService = (serviceId: string) => {
    if (!serviceId) return null;

    const { serviceTypesList, flatServicesList } = parseServiceSource();

    // New grouped shape: [{ id, name, services: [...] }] or { service_types: { ... } }
    const matchedServiceType = serviceTypesList.find((serviceType: any) => isSameId(serviceType?.id, serviceId));
    if (matchedServiceType) {
      return {
        service_id: matchedServiceType?.id,
        service_name: matchedServiceType?.name,
        sub_services: flattenSubServices(matchedServiceType?.services ?? []),
      };
    }

    // Legacy flat shape: [{ service_id, service_name, sub_services }]
    const directService = flatServicesList.find((service: any) => isSameId(service?.service_id, serviceId));
    if (directService) return directService;

    // If caller passed service_type_id in flat data, return service type name when available.
    const directServiceType = flatServicesList.find((service: any) => isSameId(service?.service_type_id, serviceId));
    if (directServiceType) {
      return {
        service_id: directServiceType?.service_type_id,
        service_name: directServiceType?.service_type_name ?? directServiceType?.service_name,
        sub_services: [],
      };
    }

    for (const serviceType of serviceTypesList) {
      const nestedService = (serviceType?.services ?? []).find((service: any) =>
        isSameId(service?.service_id, serviceId),
      );
      if (nestedService) return nestedService;
    }

    return null;
  };

  const getSubService = (serviceId: string, subServiceId: string) => {
    if (!subServiceId) return null;

    const { source, serviceTypesList, flatServicesList } = parseServiceSource();

    const service = getService(serviceId);
    const nestedSubService = service?.sub_services?.find((subService: any) =>
      isSameId(subService?.sub_service_id, subServiceId),
    );
    if (nestedSubService) return nestedSubService;

    // Legacy flat shape fallback
    for (const flatService of flatServicesList) {
      const directSubService = (flatService?.sub_services ?? []).find((subService: any) =>
        isSameId(subService?.sub_service_id, subServiceId),
      );
      if (directSubService) return directSubService;
    }

    for (const serviceType of serviceTypesList) {
      const isMatchingServiceType = isSameId(serviceType?.id, serviceId);
      const matchingService = (serviceType?.services ?? []).find((s: any) => isSameId(s?.service_id, serviceId));

      const servicesToSearch = isMatchingServiceType
        ? (serviceType?.services ?? [])
        : matchingService
          ? [matchingService]
          : [];

      const foundSubService = findSubServiceInServices(servicesToSearch, subServiceId);

      if (foundSubService) return foundSubService;
    }

    // If serviceId is missing/wrong, use API-provided sub_service_to_service_map when available.
    const mappedServiceId = source?.sub_service_to_service_map?.[subServiceId]?.service_id;
    if (mappedServiceId) {
      for (const serviceType of serviceTypesList) {
        const mappedService = (serviceType?.services ?? []).find((s: any) => isSameId(s?.service_id, mappedServiceId));
        const mappedSubService = (mappedService?.sub_services ?? []).find((subService: any) =>
          isSameId(subService?.sub_service_id, subServiceId),
        );
        if (mappedSubService) return mappedSubService;
      }
    }

    // Last fallback: lookup only by sub_service_id across all service types.
    for (const serviceType of serviceTypesList) {
      const groupedSubService = findSubServiceInServices(serviceType?.services ?? [], subServiceId);
      if (groupedSubService) return groupedSubService;
    }

    return null;
  };

  const getCurrentYear = () => {
    const date = new Date(Date.now());
    return date.getFullYear();
  };

  const getCurrentDate = () => {
    const date = new Date(Date.now());
    return format(date, "yyyy-MM-dd");
  };

  const addMinutesAndCalculateTime = (originalTime: string, minutesToAdd: number) => {
    const originalDate = new Date(`2000-01-01T${originalTime}:00`);
    const newDate = addMinutes(originalDate, minutesToAdd);

    const newTime = format(newDate, "HH:mm");
    const hours = newDate.getHours();
    const minutes = newDate.getMinutes();

    return {
      newTime,
      hours,
      minutes,
    };
  };

  const convertDOBToYMDFormat = (dob: string) => {
    if (!dob || dob === "" || dob === undefined || dob === null) {
      return {
        years: "0",
        months: "0",
        days: "0",
      };
    }
    // The given date in the format "YYYY-MM-DD"
    const dateString = dob;

    // Parse the input date string
    const parts = dateString.split("-");
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const day = parseInt(parts[2]); // Months are 0-based (January is 0, February is 1, etc.)

    // Create Date objects for the input date and the current date
    const inputDate = new Date(year, month, day);
    const currentDate = new Date();

    // Calculate years, months, and days using real calendar arithmetic so this
    // is a true inverse of convertYDMtoDOBformat. Averaged constants
    // (365.25 / 30.44 days) drift by a day and don't round-trip.
    const yearsPassed = differenceInYears(currentDate, inputDate);
    const monthsPassed = differenceInMonths(currentDate, addYears(inputDate, yearsPassed));
    const daysPassed = differenceInDays(currentDate, addMonths(addYears(inputDate, yearsPassed), monthsPassed));

    return {
      years: yearsPassed.toString(),
      months: monthsPassed.toString(),
      days: daysPassed.toString(),
    };
  };

  const HTMLLiTagsToArray = (htmlString: string): string[] => {
    if (htmlString === "" || htmlString === undefined || htmlString === null) return [];

    const textWithLineBreaks = htmlString.replace(/<li>(.*?)<\/li>/gm, "$1\n");

    const linesArray = textWithLineBreaks.split("\n").filter((line) => line.trim() !== "");

    const textArray = linesArray.map((line) => line.replace(/<[^>]*>?/gm, "")).filter(Boolean) ?? [];

    return textArray ?? [];
  };

  const convertWeightToKilogramGrams = (weight: string) => {
    if (weight === "") {
      return {
        kilograms: "",
        grams: "",
      };
    }
    const weightFloat = parseFloat(weight);
    const kilograms = Math.floor(weightFloat); // Extract the whole part as kilograms
    const grams = Math.round((weightFloat - kilograms) * 1000);
    return {
      kilograms: kilograms.toString(),
      grams: grams.toString(),
    };
  };

  const addKilogramsAndGrams = (kilograms: string, grams: string) => {
    const kg = kilograms !== "" ? parseInt(kilograms) : 0;
    const gm = grams !== "" ? parseInt(grams) : 0;
    return (kg + gm / 1000).toString();
  };

  const backgroundColorBeHalfOfService = (serviceId: string) => {
    if (serviceId === "2fa0dc20-2385-11ed-8ca0-1e00da26097a") {
      return `bg-[#F4FBFF] text-[#094769]`; //opd
    } else if (serviceId === "d10441ad-4549-11ed-af80-d1c5db9bbce2") {
      return `bg-[#FCEDFF] text-[#75118A] `; //grooming
    } else if (serviceId === "ce6d6191-c08a-4871-8f3b-d42f6798a8b2") {
      return `bg-[#CDDBFF] text-[#001C62]`; //teliconsultation
    } else if (serviceId === "7206babd-481b-4295-94f9-9b93c239d0bb") {
      return `bg-[#FFEEE0] text-[#B74B00]`; //surgery
    } else if (serviceId === "ae6445e6-c1cc-4e00-a586-45eb46e430c8") {
      return `bg-[#FEFFCF] text-[#6D6F00]`; //Home visit
    } else if (
      serviceId === "05bbbe4e-0292-4c1e-b46e-e20437d6c846" ||
      serviceId === "da22b4e1-4f98-4dcb-8a9e-b94ddff33712"
    ) {
      return `bg-[#DDFFDD] text-[#026200]`; //CT and Ultra and X-ray
    } else if (DIAGNOSTICS_SERVICE_IDS.includes(serviceId)) {
      return `bg-[#DDFFDD] text-[#026200]`; //Diagnostics: Pathology, X-Ray, Ultrasound, Emergency
    } else if (serviceId === "172199d0-899d-49db-af11-56ffaa50b8c7") {
      return `bg-red-300 text-[#026200]`; //CT and Ultra and X-ray
    } else if (serviceId === "059ab443-5d88-4655-9061-48a7be992c91") {
      return `bg-[#A1FFEE] text-[#004F41]`; //Phy
    } else if (serviceId === "c0ce6be0-167f-4083-af30-101727adaa93") {
      return `bg-[#F9F9F9] text-[#383838]`; //Slot Reserved
    } else {
      return `bg-[#F4FBFF] text-[#094769]`;
    }
  };
  const statusBackgroundColor = (status: string) => {
    if (status === "pending") {
      return `bg-[#FCE9B7]`;
    } else if (status === "approved") {
      return `bg-[#A9EBAF]`;
    } else if (status === "rejected") {
      return `bg-[#FDAAAA]`;
    } else {
      return `bg-[#F4FBFF]`;
    }
  };

  const InvoicestatusBackgroundColor = (status: string) => {
    if (status === "unpaid") {
      return `bg-[#FF8383]`;
      // return `bg-[#FFEDED] text-[#ED0101] ring-[#ED0101]`;
    } else if (status === "partially_paid") {
      return `bg-[#FCE9B7]`;
    } else if (status === "partially_returned") {
      return `bg-[#FFBE7E]`;
    } else if (status === "returned") {
      return `bg-[#F8BEFB]`;
    } else if (status === "paid") {
      return `bg-[#A0FFA3]`;
      // return `bg-[#F4FFF4] text-[#00770c] ring-[#00860D]`;
    } else if (status === "refund_requested") {
      return `bg-[#B7C0FC]`;
    } else if (status === "refunded") {
      return `bg-[#9ADFFF]`;
    } else if (status === "partially_refunded") {
      return `bg-[#FFEA81]`;
    } else if (status === "refund_rejected") {
      return `bg-[#FFA28E]`;
    } else if (status === "expired") {
      return `bg-[#FDC9D0]`;
    } else if (status === "open") {
      return `bg-[#C9FDDA]`;
    } else if (status === "overdue") {
      return `bg-[#FFDDAB]`;
    } else if (status === "closed") {
      return `bg-[#DEDEDE]`;
    } else if (status === "success") {
      return `bg-[#EAFACE]`;
    } else if (status === "voucher_paid") {
      return `bg-[#A0FFA3]`;
    } else {
      return `bg-[#fda4af]`;
    }
  };

  const statusButtonColor = (bookingStatus: string) => {
    if (bookingStatus === "completed") {
      return `bg-[#EAFACE] text-[#477200] border border-1 border-[#477200]`;
    } else if (bookingStatus === "attending") {
      return `bg-[#C3ECF5] text-[#09305E] border border-1 border-[#09305E]`;
    } else if (bookingStatus === "check out") {
      return `bg-[#EFFEF] text-[#006F76] border border-1 border-[#006F76]`;
    } else if (bookingStatus === "waiting") {
      return `bg-[#FFE9DD] text-[#EA5400] border border-1 border-[#EA5400]`;
    } else if (bookingStatus === "upcoming") {
      return `bg-[#FFF2D1] text-[#A47A0D] border border-1 border-[#A47A0D]`;
    } else if (bookingStatus === "no show") {
      return `bg-[#FDEDFF] text-[#6C0076] border  border-1 border-[#6C0076]`;
    } else if (bookingStatus === "cancelled") {
      return `bg-[#FFEBEB] text-[#D00000] border border-1 border-[#D00000]`;
    } else {
      return `bg-[#EAFACE] text-[#477200]`;
    }
  };

  const epocToDateTime = (epoc: string) => {
    const timestamp = epoc;
    const dateObj = new Date(timestamp);

    // Get the date components
    const year = dateObj?.getFullYear();
    const month = String(dateObj?.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed, so add 1
    const day = String(dateObj?.getDate())?.padStart(2, "0");

    // Get the time components
    let hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, "0");

    // Convert hours to 12-hour format and determine AM/PM
    const ampm = hours >= 12 ? "PM" : "AM";
    hours %= 12;
    hours = hours || 12; // Convert 0 to 12

    // Format the date and time
    const formattedDate = `${day}-${month}-${year}`;
    const formattedTime = `${hours}:${minutes} ${ampm}`;
    return `${formattedDate} ${formattedTime}`;
  };

  const epocToTime = (timestamp: any) => {
    // Convert to milliseconds
    const date = new Date(timestamp * 1000);

    // Convert to IST manually (+5:30 hours)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 minutes in ms
    const istDate = new Date(date.getTime() + istOffset);

    // Format hours in 12-hour format with AM/PM
    let hours = istDate.getHours();
    const minutes = istDate.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;
    const formattedTime = `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    return formattedTime;
  };

  // return date and am pm
  const epocToDateTimeForNumber = (epochTimestamp: number): string => {
    const date = new Date(epochTimestamp * 1000);

    // Extract date and time components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours24 = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");

    // Convert 24-hour time to 12-hour time with AM/PM
    const hours12 = hours24 % 12 || 12;
    const amPm = hours24 >= 12 ? "PM" : "AM";

    return `${year}-${month}-${day} ${hours12}:${minutes} ${amPm}`;
  };
  // return date and am pm
  const epocToDateTimeForNumberDDMMYY = (epochTimestamp: number): string => {
    const date = new Date(epochTimestamp * 1000);

    // Extract date and time components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours24 = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");

    // Convert 24-hour time to 12-hour time with AM/PM
    const hours12 = hours24 % 12 || 12;
    const amPm = hours24 >= 12 ? "PM" : "AM";

    return `${day}/${month}/${year} | ${hours12}:${minutes} ${amPm}`;
  };

  // Am pm time calculation
  function convertTo12HourFormatWithAMPM(timeString) {
    let [hour, minute] = timeString ? timeString.split(":").map(Number) : [null, null];
    const period = hour >= 12 ? "PM" : "AM";
    if (hour === 0) {
      hour = 12; // Convert 00:00 to 12:00 AM
    } else if (hour > 12) {
      hour -= 12; // Convert hours greater than 12 to 12-hour format
    }
    hour = hour < 10 ? "0" + hour : hour.toString(); // Convert hour to string and add leading zero if needed
    minute = minute < 10 ? "0" + minute : minute.toString(); // Convert minute to string and add leading zero if needed

    return `${hour}:${minute} ${period}`;
  }

  const extractTextArray = (htmlString) => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = htmlString;

    // Replace </span><span> or similar tag transitions with a space
    const fixedHTML = tempElement.innerHTML.replace(/<\/[^>]+>\s*<[^>]+>/g, " </span><span>");

    tempElement.innerHTML = fixedHTML;

    return tempElement.innerText.replace(/\s+/g, " ").trim();
  };

  function getDisplayStaffName(booking: any): string | null {
    if (!booking) return null;

    const groomerName = booking?.groomer_details?.name?.trim();
    const staffName = booking?.staff_details?.name?.trim();

    if (groomerName) return groomerName;
    if (staffName) return staffName;

    return null;
  }

  const baseChipStyle =
    "flex w-[80px] h-[27px] px-0 py-[6px] justify-center items-center gap-[3px] rounded-[10px] text-[12px] font-semibold";

  const statusStyles: Record<string, string> = {
    Attending: "bg-[#DBEAFE] text-[#1E40AF]",
    Upcoming: "bg-[#F3EDFA] text-[#8451CD]",
    "No Show": "bg-[#E6E7EB] text-[#000]",
    Waiting: "bg-[#FEF3C7] text-[#92400E]",
    Cancelled: "bg-[#FFE9EA] text-[#F10015]",
    Completed: "bg-[#DCFCE7] text-[#166534]",
    "In Transit": "bg-[#F3EDFA] text-[#8451CD]",
    Confirmed: "bg-[#FFFFFF] text-[#32AD1D]",
    Accepted: "bg-[#DCFCE7] text-[#166534]",
    Pending: "bg-[#FEF3C7] text-[#92400E]",
    Draft: "bg-[#FFF2E2] text-[#B75E00]",
  };

  const statusChipPWL = (status: string) => {
    return `${baseChipStyle} ${statusStyles[status] || "bg-[#F4FBFF] text-[#094769]"}`;
  };
  /* Redux States Utils Ends */

  const DOCUMENT_TYPE_OPTIONS = [
    { id: "1", title: "General Consent Form", sub: "" },
    { id: "2", title: "X-RAY", sub: "" },
    { id: "3", title: "ULTRASOUND", sub: "ULTRASOUND" },
    { id: "4", title: "Feline Immunodeficiency Virus / Feline Leukemia Virus Antigen Test", sub: "Report" },
    { id: "5", title: "Canine Parvo-Corona-Giardia Antigen Kit test", sub: "Report" },
    { id: "6", title: "Ehrlichia-Anaplasa-Babesia-Heartworm Antibody Kit test", sub: "Report" },
    { id: "7", title: "Canine Distemper-Parvo-Corona-Giardia Combo Rapid test", sub: "Report" },
    { id: "8", title: "Feline Panleukopenia-Feline Corona-Giardia Kit test", sub: "Report" },
    { id: "9", title: "Canine Parvo", sub: "Report" },
    { id: "10", title: "Canine Parvo Distemper", sub: "Report" },
    { id: "11", title: "Feline Infectious Peritonitis Antibody Test", sub: "Report" },
    { id: "12", title: "Feline Panleukopenia Antigen Test Kit", sub: "Report" },
    { id: "13", title: "Death Certificate", sub: "Report" },
    { id: "14", title: "EUTHANASIA CONSENT FORM", sub: "Report" },
    { id: "15", title: "FREEDOM FROM DISEASE DECLARATION", sub: "Report" },
    { id: "16", title: "PET HEALTH CUM TRAVEL CERTIFICATE", sub: "Report" },
    { id: "17", title: "MICROCHIPPING CERTIFICATE", sub: "Report" },
    { id: "18", title: "PET VACCINATION CERTIFICATE", sub: "Report" },
    { id: "19", title: "CERTIFICATE OF STERILIZATION", sub: "Report" },
    { id: "20", title: "Boarding and Hospitalisation Agreement", sub: "Report" },
    { id: "21", title: "PET HEALTH CERTIFICATE", sub: "Report" },
    { id: "22", title: "Sedation, or Anaesthesia and Surgery Consent Form", sub: "Report" },
    { id: "23", title: "X-Ray/USG Consent Form", sub: "Report" },
    { id: "24", title: "Blood glucose", sub: "Report" },
    { id: "25", title: "Urine Routine Analysis", sub: "Report" },
    { id: "26", title: "Ear Swab for Mite", sub: "Report" },
    { id: "27", title: "BP measurement", sub: "Report" },
    { id: "28", title: "Skin Scraping Microscopic", sub: "Report" },
    { id: "29", title: "ECHOCARDIOGRAPHY", sub: "Report" },
    { id: "30", title: "Ear Cytology Template", sub: "Report" },
    { id: "31", title: "Skin Cytology Template", sub: "Report" },
    { id: "32", title: "Rabies Titre", sub: "Report" },
    { id: "34", title: "Cytology", sub: "Report" },
    { id: "33", title: "FNAC / HISTOPATHOLOGY / BIOPSY", sub: "Report" },
    { id: "35", title: "Fungal Staining", sub: "Report" },
  ];

  return {
    base64ToValidURL,
    hasAnyValidValue,
    getCurrentYear,
    addKilogramsAndGrams,
    HTMLLiTagsToArray,
    convertWeightToKilogramGrams,
    addMinutesAndCalculateTime,
    convertDOBToYMDFormat,
    getPetStatusCorrespondingBooleans,
    convertYDMtoDOBformat,
    convertToValidDateStringFormat,
    maskMobile,
    fileListToBase64,
    classNames,
    convertNoteToArray,
    truncateString,
    convertTime12HrFormat,
    to12HrFormat,
    dateToAge,
    DOBToAge,
    ISOToRelativeTime,
    addAMPMToTime,
    formatShiftTimings,
    getService,
    getCurrentDate,
    getSubService,
    ISODatetoHuman,
    backgroundColorBeHalfOfService,
    statusBackgroundColor,
    statusButtonColor,
    epocToDateTime,
    convertTo12HourFormatWithAMPM,
    extractTextArray,
    convertCelciusToFarenhite,
    convertFarenhiteToCelcius,
    handleBase64ToImageDownload,
    imageUrlToDownload,
    convertDataToHTMLList,

    cleanURLPathname,
    convertToReadableFormat,
    POStatusTextColor,
    getStatusColor,
    epocToDateTimeForNumber,
    epocToTime,
    makeData,
    epocToDateTimeForNumberDDMMYY,
    dateTimeToEpoc,
    InvoicestatusBackgroundColor,
    InvoiceStatusDataTransformed,
    veticDateFormat,
    veticEpocToTime,
    formatDate,
    statusFormatLebel,
    formatTo12Hour,
    getLabStatusClasses,
    formatStatus,
    getDisplayStaffName,
    statusChipPWL,
    DOCUMENT_TYPE_OPTIONS,
  };
};

const getLabStatusClasses = (status?: string) => {
  switch (status) {
    case "pending_for_pickup":
      return "bg-[#FFF8E6] text-[#756100] ring-[#856E00]";

    case "delayed":
    case "resampling_required":
    case "rejected":
    case "sample_rejected":
      return "bg-[#FFEDED] text-[#ED0101] ring-[#ED0101]";

    case "uploaded":
    case "report_uploaded":
      return "bg-[#F4FFF4] text-[#00770c] ring-[#00860D]";

    case "sample_collected":
      return "bg-[#f4f5ff] text-[#000e77] ring-[#000086]";

    case "picked":
    case "handed_over":
      return "bg-[#FEEEDF] text-[#8e4502] ring-[#A55100]";

    case "trf_generated":
      return "bg-[#FFF8DF] text-[#715c01] ring-[#816900]";

    default:
      return "";
  }
};

const formatStatus = (status?: string) => {
  if (!status) return "-";
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export const handleActionByKey = (
  key: string,
  item: any,
  handlers: {
    handleRejectRequestClick: (item: any) => void;
    handleShrinkImageClick: (item: any) => void;
    handleInvalidImageClick: (item: any) => void;
    handleActionsClick: (item: any, key: string) => void;
  },
) => {
  if (key === "reject_request") {
    handlers.handleRejectRequestClick(item);
  } else if (key === "shrink") {
    handlers.handleShrinkImageClick(item);
  } else if (key === "invalid_image") {
    handlers.handleInvalidImageClick(item);
  } else {
    handlers.handleActionsClick(item, key);
  }
};
